import { Badge, Box, Button, Flex, Grid, Text } from "@chakra-ui/react";
import { ArrowClockwise, ArrowRight, Warning } from "@phosphor-icons/react";
import { useQueries } from "react-query";
import { useMemo } from "react";
import { useAuth } from "../../../../context";
import { FoundationDataV2 } from "../../../../api";
import { foundationV2Keys, useFoundationV2QuestionUsage } from "../../../../hooks/query/issb";
import { getFormattedNumValue } from "../../../../utils";
import { citedFigureFor, citedFiguresByVersion } from "../../../../utils/foundationCitedFigures";

/**
 * Tells a reader that this answer was written from an approved figure that has since changed.
 *
 * Renders NOTHING unless something is actually superseded — which is most of the time. A panel that appeared
 * on every answer to say "all good" would be scrolled past, and then missed on the one answer where it
 * mattered.
 *
 * It never changes the answer. The figure moved; the words a person approved did not, and deciding whether
 * they still hold is the reader's job. So the copy is an invitation to re-check, not a claim of error.
 *
 * One row per changed datapoint, in the extraction review's "differs" style: the figure this answer uses in
 * dark orange, an arrow, the latest approved figure. The usage trace carries versions but no values, so:
 *  - the USED figure is read from the answer's own citation tag (`data-foundation_figure`) — the archived
 *    version itself cannot be fetched. An answer whose text no longer cites it (or a pre-ESG-4014 citation)
 *    shows the version alone, never a guessed number;
 *  - the LATEST figure is that year's approved row (`GET /years/{year}/curated`, the same query and cache the
 *    citation drawer uses), fetched only for the years that have a changed figure.
 *
 * "Update all to latest" hands `onUpdateToLatest` the latest rows by datapoint; the page rewrites the
 * answer's citations into the editor for review and saving (it never saves by itself). Offered only when the
 * page passes the handler — i.e. the user can edit — and at least one changed figure has a latest row and
 * is cited inline, since only an inline citation can be moved.
 */
export default function FoundationUsageNotice({ questionId, answerHtml, onUpdateToLatest }) {
  const { callApi } = useAuth();
  const { data } = useFoundationV2QuestionUsage(questionId);

  const superseded = useMemo(() => (data ?? []).filter((usage) => usage.superseded), [data]);
  const years = useMemo(
    () => [...new Set(superseded.map((usage) => usage.foundationYear).filter((year) => year != null))],
    [superseded]
  );
  const yearQueries = useQueries(
    years.map((year) => ({
      queryKey: foundationV2Keys.curatedForYear(year),
      queryFn: () => callApi(FoundationDataV2.listCuratedRowsForYear, year),
      // A failed year just leaves its latest figures blank — the versions still say what moved.
      retry: false,
    }))
  );
  const latestByUid = useMemo(() => {
    const byUid = {};
    yearQueries.forEach((query) => (query.data ?? []).forEach((row) => (byUid[row.datapointUid] = row)));
    return byUid;
  }, [yearQueries]);
  const citedFigures = useMemo(() => citedFiguresByVersion(answerHtml), [answerHtml]);

  if (superseded.length === 0) return null;

  const updatable = superseded.filter(
    (usage) =>
      latestByUid[usage.datapointUid] &&
      citedFigureFor(citedFigures, usage.datapointUid, usage.usedVersion) != null
  );

  return (
    <Box mb={4} border="1px solid" borderColor="orange.200" borderRadius="12px" overflow="hidden" bg="white">
      <Flex align="center" gap={3} px={4} py={3} bg="orange.50" borderBottom="1px solid" borderColor="orange.200">
        <Box color="orange.500" flexShrink={0}>
          <Warning size={18} weight="fill" />
        </Box>
        <Box>
          <Text fontWeight="700" fontSize="14px" color="orange.800">
            Built on {superseded.length} foundation figure{superseded.length === 1 ? "" : "s"} that{" "}
            {superseded.length === 1 ? "has" : "have"} since changed
          </Text>
        </Box>
        {onUpdateToLatest && updatable.length > 0 ? (
          <Button
            ml="auto"
            size="sm"
            flexShrink={0}
            borderRadius="full"
            bg="orange.500"
            color="white"
            _hover={{ bg: "orange.600" }}
            _active={{ bg: "orange.700" }}
            leftIcon={<ArrowClockwise size={14} weight="bold" />}
            onClick={() => onUpdateToLatest(latestByUid)}
          >
            Update all to latest
          </Button>
        ) : null}
      </Flex>

      <Grid templateColumns="minmax(0, 1fr) auto 28px auto" columnGap={4} alignItems="center" px={4}>
        <ColumnHead>Datapoint</ColumnHead>
        <ColumnHead align="right">Used in this answer</ColumnHead>
        <Box />
        <ColumnHead align="right">Latest</ColumnHead>
        {superseded.map((usage) => {
          const latest = latestByUid[usage.datapointUid];
          const removed = usage.currentVersion == null;
          return (
            <Row key={`${usage.datapointUid}-${usage.usedVersion}`}>
              <Box minW={0} py={2}>
                {/* The name, now that the endpoint projects it: this read a raw UUID to users for as long as
                    FoundationQuestionUsageDto carried no dataKey. The fallback stays because an entry written
                    before the trace carried a name has none, and an IT pins that case. The year is worth the
                    characters here - one metric legitimately exists in two reporting years. */}
                <Text fontSize="13.5px" fontWeight="600" color="darkTeal" noOfLines={1}>
                  {usage.dataKey ?? usage.datapointUid}
                </Text>
                {usage.foundationYear ? (
                  <Text fontSize="11.5px" color="gray.500">
                    {usage.foundationYear}
                  </Text>
                ) : null}
              </Box>
              <Figure
                value={citedFigureFor(citedFigures, usage.datapointUid, usage.usedVersion)}
                version={usage.usedVersion}
                tone="orange.800"
              />
              <Flex justify="center" color="orange.500">
                <ArrowRight size={16} weight="bold" />
              </Flex>
              {removed ? (
                /* "removed" rather than a version number when the datapoint is gone: there is no current
                   version to name, and calling it v0 would invent one. */
                <Box textAlign="right">
                  <Badge variant="subtle" colorScheme="gray" textTransform="none">
                    Removed
                  </Badge>
                </Box>
              ) : (
                <Figure value={latestFigure(latest)} unit={latest?.unit} version={usage.currentVersion} tone="darkTeal" />
              )}
            </Row>
          );
        })}
      </Grid>
    </Box>
  );
}

/** A grid row: its cells sit straight in the parent grid, with a rule between rows. */
function Row({ children }) {
  return (
    <Box display="contents" sx={{ "& > *": { borderTop: "1px solid", borderColor: "gray.100" } }}>
      {children}
    </Box>
  );
}

function ColumnHead({ children, align = "left" }) {
  return (
    <Text fontSize="10.5px" fontWeight="800" letterSpacing="0.08em" color="gray.500" textTransform="uppercase" textAlign={align} py={2}>
      {children}
    </Text>
  );
}

/**
 * One figure and its version. `value` already carries its unit when it came off a citation tag (it is the
 * text written into the answer), so `unit` is only passed for the latest row's separate unit field.
 */
function Figure({ value, unit, version, tone }) {
  return (
    <Box textAlign="right" py={2}>
      {value ? (
        <Text fontSize="14px" fontWeight="700" color={tone} whiteSpace="nowrap" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {value}
          {unit ? (
            <Text as="span" fontSize="12px" fontWeight="500" color="gray.600" ml={1}>
              {unit}
            </Text>
          ) : null}
        </Text>
      ) : null}
      <Text fontSize="11.5px" color="gray.500" whiteSpace="nowrap">
        Version {version ?? "?"}
      </Text>
    </Box>
  );
}

/** The latest approved value, formatted as the Foundation Data table shows it. */
function latestFigure(row) {
  if (!row || row.dataValue == null || row.dataValue === "") return null;
  return row.valueNumeric != null ? getFormattedNumValue(row.valueNumeric, 2) : row.dataValue;
}
